import React, { useState, useRef, useEffect } from "react";
import ContentList from "./ContentList";

const SubjectPanel = ({
  subjects,
  exams,
  subjectContent,
  expandedSubjects,
  loading,
  onToggleSubject,
  onAddSubject,
  onEditSubject,
  onDeleteSubject,
  onAddContent,
  onEditContent,
  onDeleteContent,
  onLinkToExams,
  onLinkToMockTests
}) => {
  const [openActionMenus, setOpenActionMenus] = useState(new Set());
  const [menuPositions, setMenuPositions] = useState({});
  const menuRefs = useRef({});

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(menuRefs.current).forEach(subjectId => {
        if (menuRefs.current[subjectId] && !menuRefs.current[subjectId].contains(event.target)) {
          setOpenActionMenus(prev => {
            const newSet = new Set(prev);
            newSet.delete(subjectId);
            return newSet;
          });
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const calculateMenuPosition = (subjectId) => {
    const buttonElement = menuRefs.current[subjectId]?.querySelector('button');
    if (buttonElement) {
      const rect = buttonElement.getBoundingClientRect();
      const menuWidth = 180;
      const menuHeight = 200; // Increased for 4 menu items
      
      let top = rect.bottom + 5;
      let left = rect.right - menuWidth;
      
      if (left < 10) {
        left = rect.left;
      }
      if (top + menuHeight > window.innerHeight - 20) {
        top = rect.top - menuHeight - 5;
      }
      
      return { top, left };
    }
    return { top: 0, left: 0 };
  };

  const toggleActionMenu = (subjectId) => {
    setOpenActionMenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subjectId)) {
        newSet.delete(subjectId);
        setMenuPositions(prevPos => {
          const newPos = { ...prevPos };
          delete newPos[subjectId];
          return newPos;
        });
      } else {
        newSet.clear();
        newSet.add(subjectId);
        
        setTimeout(() => {
          const position = calculateMenuPosition(subjectId);
          setMenuPositions(prevPos => ({
            ...prevPos,
            [subjectId]: position
          }));
        }, 0);
      }
      return newSet;
    });
  };

  const closeActionMenu = (subjectId) => {
    setOpenActionMenus(prev => {
      const newSet = new Set(prev);
      newSet.delete(subjectId);
      return newSet;
    });
  };

  const handleMenuAction = (action, subject) => {
    closeActionMenu(subject._id);
    action(subject);
  };

  if (loading && subjects.length === 0) {
    return (
      <div className="col-12">
        <div className="bg-white rounded-12 p-40 text-center">
          <div className="spinner"></div>
          <p className="text-14 text-light-1 mt-20">Loading subjects...</p>
        </div>
      </div>
    );
  }

  if (subjects.length === 0) {
    return (
      <div className="col-12">
        <div className="bg-white rounded-12 p-40 text-center">
          <i className="icon-book text-60 text-light-3 mb-20"></i>
          <h5 className="text-18 fw-500 mb-10">No Subjects Yet</h5>
          <p className="text-14 text-light-1 mb-30">
            Create your first subject to get started. Subjects can be linked to multiple exams with different pricing.
          </p>
          <button 
            onClick={onAddSubject}
            className="button -md bg-purple-1 text-white fw-500"
          >
            <i className="icon-plus mr-10"></i>
            Create First Subject
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="col-12">
      <div className="bg-white rounded-12 p-20">
        <div className="d-flex items-center justify-between mb-20">
          <div>
            <h5 className="text-16 fw-500">All Subjects</h5>
            <p className="text-13 text-light-1 mt-5">
              Manage subjects and their content. Link subjects to exams with pricing.
            </p>
          </div>
          <button 
            onClick={onAddSubject}
            className="button -sm bg-purple-1 text-white"
          >
            <i className="icon-plus mr-8"></i>
            Add Subject
          </button>
        </div>

        <div 
          className="subjects-grid" 
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '15px'
          }}
        >
          {subjects.map((subject) => {
            const content = subjectContent[subject._id];
            const isExpanded = expandedSubjects.has(subject._id);
            const examCount = subject.exams?.length || 0;

            return (
              <div 
                key={subject._id} 
                className="subject-item bg-white rounded-8"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden'
                }}
              >
                {/* Subject Row */}
                <div 
                  className="d-flex items-center justify-between px-20 py-15 cursor-pointer hover:bg-light-5 transition-all overflow-hidden"
                  onClick={() => onToggleSubject(subject._id)}
                  style={{ minWidth: 0 }}
                >
                  <div className="d-flex items-center flex-1 min-w-0 overflow-hidden">
                    <div className="d-flex items-center mr-15">
                      <i className={`text-14 ${isExpanded ? 'icon-chevron-down' : 'icon-chevron-right'} text-light-1 mr-10`}></i>
                      <div 
                        className="size-30 d-flex justify-center items-center rounded-6 mr-15"
                        style={{backgroundColor: subject.color + '20', color: subject.color}}
                      >
                        <i className={`text-12 ${subject.icon || 'icon-book'}`}></i>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-15 fw-500 mb-3 d-flex items-center x-gap-8 min-w-0">
                        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={subject.title}>{subject.title}</span>
                        {examCount > 0 && (
                          <span className="badge bg-blue-1 text-blue" style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '10px', flexShrink: 0 }}>
                            {examCount}
                          </span>
                        )}
                      </div>
                      <div className="text-12 text-light-1">
                        <div className="mb-3" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={subject.description || 'No description'}>{subject.description || 'No description'}</div>
                        {/* Show which exams this subject belongs to */}
                        {examCount > 0 && exams && (
                          <div className="d-flex items-center x-gap-5 flex-wrap mb-3 overflow-hidden">
                            {(subject.exams || []).slice(0, 3).map((examRef) => {
                              const examId = examRef._id || examRef;
                              const exam = exams.find(e => e._id === examId);
                              if (!exam) return null;
                              return (
                                <span 
                                  key={examId}
                                  className="badge"
                                  style={{ 
                                    fontSize: '11px', 
                                    padding: '4px 10px', 
                                    borderRadius: '12px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    maxWidth: '140px',
                                    backgroundColor: exam.color + '15',
                                    color: exam.color,
                                    border: `1px solid ${exam.color}30`,
                                    fontWeight: '500',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={exam.title}
                                >
                                  <i className={`text-12 ${exam.icon || 'icon-graduation-cap'} shrink-0`} style={{ color: exam.color }}></i>
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{exam.title}</span>
                                </span>
                              );
                            })}
                            {examCount > 3 && (
                              <span className="text-11 text-light-2 fw-500">+{examCount - 3} more</span>
                            )}
                          </div>
                        )}
                        <div className="d-flex items-center x-gap-10 text-11">
                          <span>• {subject.counts?.videos || 0} videos</span>
                          <span>• {subject.counts?.materails || 0} materails</span>
                          <span>• {subject.counts?.mockTests || 0} mock tests</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Menu - flexShrink: 0 ensures button stays visible */}
                  <div 
                    className="position-relative"
                    ref={el => menuRefs.current[subject._id] = el}
                    style={{ flexShrink: 0, marginLeft: '8px' }}
                  >
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleActionMenu(subject._id);
                      }}
                      className="size-30 d-flex justify-center items-center bg-light-4 rounded-full hover:bg-light-3 transition-all"
                      aria-label="Actions"
                      aria-expanded={openActionMenus.has(subject._id)}
                      aria-haspopup="true"
                      style={{color: '#6b7280', flexShrink: 0}}
                    >
                      <i className="icon-menu-vertical text-12" style={{color: '#374151'}}></i>
                    </button>
                    
                    {/* Action Menu Dropdown */}
                    {openActionMenus.has(subject._id) && menuPositions[subject._id] && (
                      <div 
                        className="position-fixed bg-white border-light rounded-8 shadow-4 overflow-hidden" 
                        style={{
                          minWidth: '180px',
                          zIndex: 9999,
                          top: `${menuPositions[subject._id].top}px`,
                          left: `${menuPositions[subject._id].left}px`
                        }}
                      >
                        <div className="py-5">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuAction(() => onAddContent(subject, 'video'), subject);
                            }}
                            className="w-100 d-flex items-center px-20 py-12 text-left hover:bg-light-6 transition-all border-0 bg-transparent"
                          >
                            <i className="icon-plus text-14 mr-12 text-green-1"></i>
                            <span className="text-14 fw-500">Add Content</span>
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuAction(() => onLinkToExams(subject), subject);
                            }}
                            className="w-100 d-flex items-center px-20 py-12 text-left hover:bg-light-6 transition-all border-0 bg-transparent"
                          >
                            <i className="icon-link text-14 mr-12 text-purple-1"></i>
                            <span className="text-14 fw-500">Link to Exams</span>
                          </button>
                          {onLinkToMockTests && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMenuAction(() => onLinkToMockTests(subject), subject);
                              }}
                              className="w-100 d-flex items-center px-20 py-12 text-left hover:bg-light-6 transition-all border-0 bg-transparent"
                            >
                              <i className="icon-clipboard-list text-14 mr-12 text-red-1"></i>
                              <span className="text-14 fw-500">Link to Mock Tests</span>
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuAction(() => onEditSubject(subject), subject);
                            }}
                            className="w-100 d-flex items-center px-20 py-12 text-left hover:bg-light-6 transition-all border-0 bg-transparent"
                          >
                            <i className="icon-edit text-14 mr-12 text-blue-1"></i>
                            <span className="text-14 fw-500">Edit Subject</span>
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuAction(() => onDeleteSubject(subject._id), subject);
                            }}
                            className="w-100 d-flex items-center px-20 py-12 text-left hover:bg-red-6 transition-all border-0 bg-transparent text-red-1"
                          >
                            <i className="icon-bin text-14 mr-12"></i>
                            <span className="text-14 fw-500">Delete Subject</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Content (Expanded Subject Content) */}
                {isExpanded && (
                  <ContentList
                    subject={subject}
                    content={content}
                    onAddContent={(contentType) => onAddContent(subject, contentType)}
                    onEditContent={(contentType, contentItem) => onEditContent(subject, contentType, contentItem)}
                    onDeleteContent={(contentId, contentType) => onDeleteContent(contentId, contentType, subject._id)}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SubjectPanel;

